import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getCustomerId, getStoredUser } from '../auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from '../components/page-elements';

const renderValue = (value, propKey = null, fallback = 'N/A') => {
  if (!value) return fallback;
  if (typeof value === 'object') {
    if (propKey && value[propKey]) return value[propKey];
    if (value.fullName) return value.fullName;
    if (value.firstName || value.lastName) return `${value.firstName || ''} ${value.lastName || ''}`.trim();
    return fallback;
  }
  return value;
};
const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'N/A';
  return dt.toLocaleString();
};

const formatAddress = (entity = {}, fields = []) => {
  const line1 = fields.map((f) => entity?.[f]).filter(Boolean).join(', ');
  return line1 || 'N/A';
};

const LIABILITY_ACKNOWLEDGMENT = 'LIABILITY ACKNOWLEDGMENT: BY SIGNING ABOVE AND/OR ACCEPTING POSSESSION OF THE LOAD, DRIVER AND CARRIER ACKNOWLEDGE THAT THE MATERIALS HAVE BEEN LOADED BY THE RAILROAD IN APPARENT GOOD ORDER AND CONDITION, UNLESS OTHERWISE NOTED AT THE TIME OF LOADING. FROM THAT MOMENT FORWARD, DRIVER AND CARRIER ASSUME FULL AND EXCLUSIVE RESPONSIBILITY AND LIABILITY FOR THE CUSTODY, SECUREMENT, TRANSPORT, AND DELIVERY OF THE MATERIALS. THE RAILROAD SHALL HAVE NO LIABILITY FOR ANY LOSS, DAMAGE, CONTAMINATION, SPILLAGE, DELAY, OR CLAIM OF ANY KIND ARISING AFTER DRIVER ACCEPTS THE LOAD, REGARDLESS OF CAUSE. CARRIER AGREES TO INDEMNIFY, DEFEND, AND HOLD HARMLESS THE RAILROAD, ITS AFFILIATES, AND EMPLOYEES FROM ANY AND ALL SUCH CLAIMS, DAMAGES, OR EXPENSES.';

const getBolStatusChipProps = (status) => {
  switch (status) {
    case 'Draft':
      return { color: 'warning', variant: 'filled' };
    case 'Completed':
      return { color: 'success', variant: 'filled' };
    default:
      return { color: 'default', variant: 'filled' };
  }
};

const BOLs = () => {
  const [bols, setBols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('completedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deletingBolId, setDeletingBolId] = useState('');

  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const customerId = getCustomerId(user);
  const isCustomer = user?.role === 'customer';
  const isInternal = user?.role === 'internal' || user?.role === 'admin';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const params = new URLSearchParams();
    if (isCustomer && customerId) params.set('customerId', customerId);

    fetch(`${API_BASE_URL}/api/bols${params.toString() ? `?${params.toString()}` : ''}`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then((data) => {
        setBols(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [isCustomer, customerId]);

  useEffect(() => {
    if (!isInternal) return;

    fetch(`${API_BASE_URL}/api/customers`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load customers');
        return response.json();
      })
      .then((data) => setCustomers(Array.isArray(data) ? data : []))
      .catch(() => setCustomers([]));
  }, [isInternal]);

  const filteredBols = useMemo(() => {
    const toDateOnlyKey = (value) => {
      if (!value) return '';
      const dt = new Date(value);
      if (Number.isNaN(dt.getTime())) return '';
      return dt.toISOString().slice(0, 10);
    };

    const base = bols.filter((bol) => {
      if (!isInternal) return true;

      const bolStatus = String(bol.status || 'Draft');
      if (statusFilter !== 'all' && bolStatus !== statusFilter) return false;

      const bolCustomerId = String(
        bol.orderNumber?.customerName?._id
        || bol.orderNumber?.customerName
        || bol.customerName?._id
        || bol.customerName
        || ''
      );
      if (customerFilter !== 'all' && bolCustomerId !== String(customerFilter)) return false;

      const bolSource = String(bol.inventorySource || 'railcar');
      if (sourceFilter !== 'all' && bolSource !== sourceFilter) return false;

      const bolDateKey = toDateOnlyKey(bol.bolDate || bol.completedAt || bol.createdAt);
      if (dateFrom && (!bolDateKey || bolDateKey < dateFrom)) return false;
      if (dateTo && (!bolDateKey || bolDateKey > dateTo)) return false;

      return true;
    });

    const term = searchTerm.trim().toLowerCase();
    if (!term) return base;

    return base.filter((bol) => {
      const orderNumber = String(bol.orderNumber?.orderNumber || '').toLowerCase();
      const customer = String(renderValue(bol.orderNumber?.customerName, 'customerName', '')).toLowerCase();
      const railcar = String(bol.railcarID || '').toLowerCase();
      const truck = String(bol.truckID || '').toLowerCase();
      const status = String(bol.status || '').toLowerCase();
      const source = String(bol.inventorySource || 'railcar').toLowerCase();
      return (
        orderNumber.includes(term) ||
        customer.includes(term) ||
        railcar.includes(term) ||
        truck.includes(term) ||
        status.includes(term) ||
        source.includes(term)
      );
    });
  }, [bols, searchTerm, isInternal, statusFilter, customerFilter, sourceFilter, dateFrom, dateTo]);

  const getSortValue = (bol, key) => {
    switch (key) {
      case 'status':
        return bol.status || 'Draft';
      case 'orderNumber':
        return bol.orderNumber?.orderNumber || '';
      case 'bolDate':
        return bol.bolDate ? new Date(bol.bolDate).getTime() : 0;
      case 'customer':
        return renderValue(bol.orderNumber?.customerName, 'customerName', '');
      case 'railcarID':
        return bol.railcarID || '';
      case 'truckID':
        return bol.truckID || '';
      case 'netWeight':
        return Number(bol.netWeight) || 0;
      case 'completedAt':
        return bol.completedAt ? new Date(bol.completedAt).getTime() : 0;
      default:
        return '';
    }
  };

  const sortedBols = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filteredBols].sort((a, b) => {
      const aVal = getSortValue(a, sortBy);
      const bVal = getSortValue(b, sortBy);

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * dir;
      }

      return String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * dir;
    });
  }, [filteredBols, sortBy, sortDirection]);

  const pagedBols = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedBols.slice(start, start + rowsPerPage);
  }, [sortedBols, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, sortBy, sortDirection, rowsPerPage, bols.length, statusFilter, customerFilter, sourceFilter, dateFrom, dateTo]);

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(columnKey);
    setSortDirection(columnKey === 'completedAt' ? 'desc' : 'asc');
  };
  const handleViewBOL = (bol) => navigate(`/bols/${bol._id}`);
  const handleDeleteDraftBol = async (bol) => {
    if (!bol?._id) return;
    if (bol.status !== 'Draft') return;

    const orderNumber = bol.orderNumber?.orderNumber || bol._id;
    const confirmed = window.confirm(`Delete draft BOL ${orderNumber}? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingBolId(String(bol._id));
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/bols/${bol._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(true),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to delete draft BOL');
      }

      setBols((prev) => prev.filter((item) => String(item._id) !== String(bol._id)));
    } catch (err) {
      setError(err.message || 'Failed to delete draft BOL');
    } finally {
      setDeletingBolId('');
    }
  };

  const handlePrintBOL = (bol) => {
    const order = bol.orderNumber || {};
    const customer = order.customerName || bol.customerName || {};
    const receiver = order.receiverName || {};
    const project = order.projectName || {};

    const customerAddress = formatAddress(customer, [
      'customerAddress1',
      'customerAddress2',
      'customerCity',
      'customerState',
      'customerZip',
    ]);

    const receiverAddress = project.fullAddress || formatAddress(receiver, [
      'billingAddress1',
      'billingAddress2',
      'billingCity',
      'billingState',
      'billingZip',
    ]);

    const customerLogo = customer.customerLogo || '';
    const isSplitLoad = Boolean(bol.splitLoad && bol.secondaryRailcarID);
    const primaryNetWeight = bol.primaryNetWeight ?? bol.netWeight;
    const primaryTonWeight = bol.primaryTonWeight ?? (primaryNetWeight != null ? Number(primaryNetWeight) / 2000 : null);

    const printContent = `
      <html>
        <head>
          <title>BOL ${bol.orderNumber?.orderNumber || bol.orderNumber}</title>
          <style>
            @page { size: auto; margin: 0.35in; }
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; margin: 0; color: #1f2937; font-size: 12px; line-height: 1.25; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
            .title-wrap h2 { margin: 0 0 2px 0; font-size: 18px; }
            .meta { font-size: 11px; color: #4b5563; margin: 1px 0; }
            .logo { max-height: 52px; max-width: 170px; object-fit: contain; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px; }
            .card { border: 1px solid #d1d5db; border-radius: 6px; padding: 6px 8px; }
            .card h3 { margin: 0 0 4px 0; font-size: 12px; color: #111827; }
            .card p { margin: 2px 0; font-size: 11px; }
            .signature-wrap { margin-top: 8px; border: 1px solid #d1d5db; border-radius: 6px; padding: 6px 8px; }
            .signature-wrap h3 { margin: 0 0 4px 0; font-size: 12px; color: #111827; }
            .signature-meta { font-size: 11px; margin-bottom: 4px; }
            .signature-image { max-width: 250px; max-height: 70px; border: 1px solid #d1d5db; border-radius: 4px; background: #fff; }
            .disclaimer { margin-top: 8px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 9px; line-height: 1.25; color: #374151; background: #f9fafb; }
            table { width: 100%; border-collapse: collapse; margin-top: 6px; }
            th, td { border: 1px solid #d1d5db; padding: 5px 6px; text-align: left; font-size: 11px; }
            th { background: #f9fafb; width: 30%; }
            .details-grid { display: grid; grid-template-columns: ${isSplitLoad ? '1fr 1fr' : '1fr'}; gap: 8px; margin-top: 6px; }
            .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 6px; }
            .split-col h3 { margin: 3px 0 0 0; font-size: 12px; color: #111827; }
            .totals-table { margin-top: 8px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title-wrap">
              <h2>BOL ${order.orderNumber || bol.orderNumber?.orderNumber || bol.orderNumber || 'N/A'}</h2>
              <div class="meta">Date: ${bol.bolDate ? new Date(bol.bolDate).toLocaleString() : 'N/A'}</div>
              <div class="meta">Status: ${bol.status || 'Draft'}</div>
            </div>
            ${customerLogo ? `<img class="logo" src="${customerLogo}" alt="Customer Logo" />` : ''}
          </div>

          <div class="grid">
            <div class="card">
              <h3>Customer</h3>
              <p><strong>Name:</strong> ${renderValue(customer, 'customerName')}</p>
              <p><strong>Address:</strong> ${customerAddress}</p>
              <p><strong>Origin:</strong> West Tennessee Railroad, 1061 James Buchanan Dr, Jackson, TN 38301</p>
            </div>
            <div class="card">
              <h3>Receiver / Delivery</h3>
              <p><strong>Receiver:</strong> ${renderValue(receiver, 'receiverName')}</p>
              <p><strong>Location:</strong> ${renderValue(project, 'projectName')}</p>
              <p><strong>Address:</strong> ${receiverAddress}</p>
            </div>
          </div>

          <div class="meta-grid">
            <table>
              <tr><th>Order Number</th><td>${order.orderNumber || bol.orderNumber?.orderNumber || bol.orderNumber || 'N/A'}</td></tr>
              <tr><th>Material</th><td>${renderValue(order.materialName, 'materialName')}</td></tr>
            </table>
            <table>
              <tr><th>Split Load</th><td>${isSplitLoad ? 'Yes' : 'No'}</td></tr>
              <tr><th>Truck / Trailer</th><td>${bol.truckID || 'N/A'} / ${bol.trailerID || 'N/A'}</td></tr>
            </table>
          </div>

          <div class="details-grid">
            <div class="split-col">
              <h3>Primary Load</h3>
              <table>
                <tr><th>Railcar ID</th><td>${bol.railcarID || 'N/A'}</td></tr>
                <tr><th>Gross Weight</th><td>${bol.grossWeight || 'N/A'}</td></tr>
                <tr><th>Tare Weight</th><td>${bol.tareWeight || 'N/A'}</td></tr>
                <tr><th>Net Weight</th><td>${primaryNetWeight || 'N/A'}</td></tr>
                <tr><th>Ton Weight</th><td>${primaryTonWeight || 'N/A'}</td></tr>
              </table>
            </div>
            ${isSplitLoad ? `
              <div class="split-col">
                <h3>Secondary Load</h3>
                <table>
                  <tr><th>Railcar ID</th><td>${bol.secondaryRailcarID || 'N/A'}</td></tr>
                  <tr><th>Gross Weight</th><td>${bol.secondaryGrossWeight || 'N/A'}</td></tr>
                  <tr><th>Tare Weight</th><td>${bol.secondaryTareWeight || 'N/A'}</td></tr>
                  <tr><th>Net Weight</th><td>${bol.secondaryNetWeight || 'N/A'}</td></tr>
                  <tr><th>Ton Weight</th><td>${bol.secondaryTonWeight || 'N/A'}</td></tr>
                </table>
              </div>
            ` : ''}
          </div>

          <table class="totals-table">
            <tr><th>Total Net Weight</th><td>${bol.netWeight || 'N/A'}</td></tr>
            <tr><th>Total Ton Weight</th><td>${bol.tonWeight || 'N/A'}</td></tr>
          </table>

          <div class="signature-wrap">
            <h3>Driver Signature</h3>
            <div class="signature-meta"><strong>Driver:</strong> ${bol.driverName || 'N/A'}</div>
            <div class="signature-meta"><strong>Signed At:</strong> ${bol.signedAt ? new Date(bol.signedAt).toLocaleString() : 'N/A'}</div>
            ${bol.driverSignatureImage ? `<img class="signature-image" src="${bol.driverSignatureImage}" alt="Driver Signature" />` : '<div class="signature-meta">No signature on file</div>'}
          </div>

          <div class="disclaimer">${LIABILITY_ACKNOWLEDGMENT}</div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();

    const triggerPrint = () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => printWindow.close(), 300);
    };

    const waitForImages = () => {
      const images = Array.from(printWindow.document.images || []);
      if (images.length === 0) {
        triggerPrint();
        return;
      }

      let doneCount = 0;
      const done = () => {
        doneCount += 1;
        if (doneCount >= images.length) {
          triggerPrint();
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          done();
          return;
        }
        img.onload = done;
        img.onerror = done;
      });

      // Fallback: do not block printing forever on browser-specific image behavior.
      setTimeout(() => {
        if (doneCount < images.length) {
          triggerPrint();
        }
      }, 1200);
    };

    if (printWindow.document.readyState === 'complete') {
      waitForImages();
    } else {
      printWindow.onload = waitForImages;
    }
  };

  if (loading) return <LoadingPane label="Loading BOLs..." />;

  return (
    <Box p={2}>
      <PageHeader
        title={isInternal ? 'BOL Queue' : 'BOLs'}
        subtitle="Search, print, and track completed load tickets."
        actions={
          isInternal ? (
            <Button variant="outlined" onClick={() => navigate('/bols/reports/truck-weigh-ins')}>
              Truck Weigh-In Report
            </Button>
          ) : null
        }
      />
      <ErrorBanner message={error} />
      {isInternal && (
        <Card sx={{ mt: 1.5, mb: 2, borderRadius: 2, border: '1px solid var(--ui-border)' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Filters
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="bol-status-filter-label">Status</InputLabel>
                <Select
                  labelId="bol-status-filter-label"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="Draft">Draft</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="bol-customer-filter-label">Customer</InputLabel>
                <Select
                  labelId="bol-customer-filter-label"
                  label="Customer"
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                >
                  <MenuItem value="all">All Customers</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer._id} value={String(customer._id)}>
                      {customer.customerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="bol-source-filter-label">Source</InputLabel>
                <Select
                  labelId="bol-source-filter-label"
                  label="Source"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  <MenuItem value="all">All Sources</MenuItem>
                  <MenuItem value="railcar">Railcar</MenuItem>
                  <MenuItem value="ground">Ground Inventory</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Date From"
                type="date"
                size="small"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Date To"
                type="date"
                size="small"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </CardContent>
        </Card>
      )}
      <TextField
        label="Search BOLs"
        placeholder="Order #, customer, railcar, truck, status"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth
        margin="normal"
      />
      {sortedBols.length === 0 ? (
        <EmptyPane title="No BOLs found" subtitle="Completed load tickets will appear here." />
      ) : isMobile ? (
        <Stack spacing={1.2}>
          {pagedBols.map((bol) => (
            <Card
              key={bol._id}
              variant="outlined"
              className="surface-hover"
              sx={{ borderRadius: 2, cursor: 'pointer' }}
              onClick={() => handleViewBOL(bol)}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {bol.orderNumber?.orderNumber || 'N/A'}
                  </Typography>
                  <Chip label={bol.status || 'Draft'} size="small" {...getBolStatusChipProps(bol.status || 'Draft')} />
                </Stack>
                <Typography variant="body2" color="text.secondary">Railcar: {bol.railcarID || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary">Truck: {bol.truckID || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary">Net Weight: {bol.netWeight || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Completed: {formatDateTime(bol.completedAt)}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    color="info"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrintBOL(bol);
                    }}
                  >
                    Print
                  </Button>
                  {isInternal && bol.status !== 'Completed' && (
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/bols/complete/${bol._id}`);
                      }}
                    >
                      Complete
                    </Button>
                  )}
                  {isInternal && bol.status === 'Draft' && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={deletingBolId === String(bol._id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDraftBol(bol);
                      }}
                    >
                      {deletingBolId === String(bol._id) ? 'Deleting...' : 'Delete'}
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} className="surface-hover" sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel active={sortBy === 'status'} direction={sortBy === 'status' ? sortDirection : 'asc'} onClick={() => handleSort('status')}>
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'orderNumber'} direction={sortBy === 'orderNumber' ? sortDirection : 'asc'} onClick={() => handleSort('orderNumber')}>
                    Order Number
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'bolDate'} direction={sortBy === 'bolDate' ? sortDirection : 'asc'} onClick={() => handleSort('bolDate')}>
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'customer'} direction={sortBy === 'customer' ? sortDirection : 'asc'} onClick={() => handleSort('customer')}>
                    Customer
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'railcarID'} direction={sortBy === 'railcarID' ? sortDirection : 'asc'} onClick={() => handleSort('railcarID')}>
                    Railcar
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'truckID'} direction={sortBy === 'truckID' ? sortDirection : 'asc'} onClick={() => handleSort('truckID')}>
                    Truck
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'netWeight'} direction={sortBy === 'netWeight' ? sortDirection : 'asc'} onClick={() => handleSort('netWeight')}>
                    Net Weight
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'completedAt'} direction={sortBy === 'completedAt' ? sortDirection : 'desc'} onClick={() => handleSort('completedAt')}>
                    Completed At
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedBols.map((bol) => (
                <TableRow key={bol._id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewBOL(bol)}>
                  <TableCell>
                    <Chip
                      label={bol.status || 'Draft'}
                      size="small"
                      {...getBolStatusChipProps(bol.status || 'Draft')}
                    />
                  </TableCell>
                  <TableCell>{bol.orderNumber?.orderNumber || 'N/A'}</TableCell>
                  <TableCell>{formatDateTime(bol.bolDate)}</TableCell>
                  <TableCell>{renderValue(bol.orderNumber?.customerName, 'customerName')}</TableCell>
                  <TableCell>{bol.railcarID || 'N/A'}</TableCell>
                  <TableCell>{bol.truckID || 'N/A'}</TableCell>
                  <TableCell>{bol.netWeight || 'N/A'}</TableCell>
                  <TableCell>{formatDateTime(bol.completedAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      color="info"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrintBOL(bol);
                      }}
                      sx={{ mr: 1 }}
                    >
                      Print
                    </Button>
                    {isInternal && bol.status !== 'Completed' && (
                      <Button
                        color="success"
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/bols/complete/${bol._id}`);
                        }}
                      >
                        Complete BOL
                      </Button>
                    )}
                    {isInternal && bol.status === 'Draft' && (
                      <Button
                        color="error"
                        variant="outlined"
                        disabled={deletingBolId === String(bol._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDraftBol(bol);
                        }}
                        sx={{ ml: 1 }}
                      >
                        {deletingBolId === String(bol._id) ? 'Deleting...' : 'Delete'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={sortedBols.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}
      {isMobile && sortedBols.length > rowsPerPage ? (
        <TablePagination
          component="div"
          count={sortedBols.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      ) : null}
    </Box>
  );
};

export default BOLs;

